import { Input } from "@remo-co/ui-core/src/components/Input";
import { InputLabel } from "@remo-co/ui-core/src/components/InputLabel";
import { RadioGroup } from "@remo-co/ui-core/src/components/RadioGroup";
import { FormGroup } from "@remo-co/ui-core/src/components/FormGroup";
import { Radio } from "@remo-co/ui-core/src/components/Radio";
import { Checkbox } from "@remo-co/ui-core/src/components/Checkbox";
import { FormControlLabel } from "@remo-co/ui-core/src/components/FormControlLabel";
import { useI18n } from "i18n";
import {
  NetworkingRecommendationQuestionType,
  QuestionType,
} from "modules/registration/constants";
import { ICustomRegistrationQuestion } from "modules/registration/types";
import { ChangeEvent, useState } from "react";
import { useStyles } from "./styles";

interface Props {
  question: ICustomRegistrationQuestion;
  onAnswerChange: (id: string, value: string) => void;
}

export const CustomQuestion = ({ question, onAnswerChange }: Props) => {
  const [multiSelectValue, setMultiSelectValue] = useState<string[]>([]);
  const { t } = useI18n(["eventLanding", "eventForm"]);
  const styles = useStyles();
  const questionId = `question-${question._id}`;

  const onMultiSelectChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked
      ? [...multiSelectValue, event.target.value]
      : multiSelectValue.filter((value) => value !== event.target.value);

    setMultiSelectValue(newValue);
    onAnswerChange(question._id, newValue.join(","));
  };

  const getInputLabel = () => (
    <InputLabel htmlFor={questionId} className={styles.inputLabel}>
      {question.question}
      {!question.isRequired && <i> - {t("eventForm:optional.indicator")}</i>}
    </InputLabel>
  );

  const renderInputField = (
    placeholder: string,
    maxLength: number,
    rows = 1,
  ) => (
    <Input
      size={rows > 1 ? "multi" : "sm"}
      fullWidth
      multiline={rows > 1}
      rows={rows}
      name={questionId}
      id={questionId}
      placeholder={placeholder}
      onChange={(event) => onAnswerChange(question._id, event.target.value)}
      inputProps={{
        maxLength,
        "data-testid": questionId,
      }}
      inputClassName={styles.input}
    />
  );

  const renderSelectOptions = () => (
    <FormGroup>
      {question.options?.map((option) => (
        <FormControlLabel
          key={option}
          value={option}
          label={option}
          control={
            <Checkbox
              color="primary"
              size="small"
              checked={multiSelectValue.includes(option)}
              onChange={onMultiSelectChange}
              name={option}
              className={styles.radio}
              data-testid={`option-${option}`}
            />
          }
        />
      ))}
    </FormGroup>
  );

  const placeholderMap: Record<NetworkingRecommendationQuestionType, string> = {
    [NetworkingRecommendationQuestionType.demands]: t(
      "eventLanding:networking.question.demands.placeholder",
    ),
    [NetworkingRecommendationQuestionType.supplies]: t(
      "eventLanding:networking.question.supplies.placeholder",
    ),
    [NetworkingRecommendationQuestionType.linkedIn]: t(
      "eventLanding:networking.question.linkedin.placeholder",
    ),
    [NetworkingRecommendationQuestionType.companyUrl]: t(
      "eventLanding:networking.question.company.url.placeholder",
    ),
  };

  const maxLengthMap: Record<NetworkingRecommendationQuestionType, number> = {
    [NetworkingRecommendationQuestionType.demands]: 300,
    [NetworkingRecommendationQuestionType.supplies]: 300,
    [NetworkingRecommendationQuestionType.linkedIn]: 100,
    [NetworkingRecommendationQuestionType.companyUrl]: 100,
  };
  const questionType =
    question.networkingRecommendationQuestionType || question.questionType;

  if (
    question.isNetworkingQuestion &&
    questionType &&
    questionType in placeholderMap
  ) {
    return (
      <div>
        {getInputLabel()}
        {renderInputField(
          placeholderMap[questionType as NetworkingRecommendationQuestionType],
          maxLengthMap[questionType as NetworkingRecommendationQuestionType],
          questionType === NetworkingRecommendationQuestionType.demands ||
            questionType === NetworkingRecommendationQuestionType.supplies
            ? 3
            : 1,
        )}
      </div>
    );
  }

  switch (question.type) {
    case QuestionType.PARAGRAPH_TEXT:
      return (
        <div>
          {getInputLabel()}
          {renderInputField(t("eventLanding:answer.placeholder"), 300, 3)}
        </div>
      );

    case QuestionType.SINGLE_SELECT:
      return (
        <div>
          {getInputLabel()}
          <RadioGroup
            onChange={(e, value) => onAnswerChange(question._id, value)}
          >
            {question.options?.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                label={option}
                control={
                  <Radio
                    color="primary"
                    size="small"
                    className={styles.radio}
                    data-testid={`option-${option}`}
                  />
                }
              />
            ))}
          </RadioGroup>
        </div>
      );

    case QuestionType.MULTI_SELECT:
      return (
        <div>
          {getInputLabel()}
          {renderSelectOptions()}
        </div>
      );

    case QuestionType.SINGLE_LINE_TEXT:
    default:
      return (
        <div>
          {getInputLabel()}
          {renderInputField(t("eventLanding:answer.placeholder"), 100)}
        </div>
      );
  }
};
